import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Tabs,
  Tab,
  Box,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { VerifiedEntry } from '@/types/api';
import { useCreateEntry } from '@/hooks/useEntries';
import { useCreateMissingEntry } from '@/hooks/useLibraryReview';
import { useLibraries } from '@/hooks/useLibraries';

interface QuickEntryDialogProps {
  open: boolean;
  chatId: number;
  libraryId: number | null;
  beforeMessageId: number;
  afterMessageId: number;
  onClose: () => void;
  onCreated: (entry: VerifiedEntry) => void;
}

type TabMode = 'regular' | 'missing';

interface TabPanelProps {
  children?: React.ReactNode;
  index: TabMode;
  value: TabMode;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`quick-entry-tabpanel-${index}`}
      aria-labelledby={`quick-entry-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const QuickEntryDialog: React.FC<QuickEntryDialogProps> = ({
  open,
  chatId,
  libraryId: initialLibraryId,
  beforeMessageId,
  afterMessageId,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation('components');
  const { data: libraries = [] } = useLibraries();
  const createEntry = useCreateEntry();
  const createMissingEntry = useCreateMissingEntry();

  const [mode, setMode] = useState<TabMode>('regular');
  const [validationError, setValidationError] = useState('');

  // Regular entry fields
  const [titleMessageId, setTitleMessageId] = useState('');
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [msNo, setMsNo] = useState('');
  const [libraryId, setLibraryId] = useState<number | null>(initialLibraryId);
  const [notes, setNotes] = useState('');

  // Missing entry fields
  const [missingMsNo, setMissingMsNo] = useState('');
  const [missingTitle, setMissingTitle] = useState('');
  const [missingAuthor, setMissingAuthor] = useState('');
  const [missingLibraryId, setMissingLibraryId] = useState<number | null>(initialLibraryId);
  const [missingNotes, setMissingNotes] = useState('');

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setMode('regular');
      setValidationError('');

      // Suggest a message ID between the two messages
      const suggestedMessageId = Math.floor((beforeMessageId + afterMessageId) / 2);
      setTitleMessageId(suggestedMessageId.toString());
      setTitle('');
      setAuthor('');
      setMsNo('');
      setLibraryId(initialLibraryId);
      setNotes('');

      setMissingMsNo('');
      setMissingTitle('');
      setMissingAuthor('');
      setMissingLibraryId(initialLibraryId);
      setMissingNotes('');
    }
  }, [open, beforeMessageId, afterMessageId, initialLibraryId]);

  const isLoading = createEntry.isPending || createMissingEntry.isPending;

  const handleCreateRegular = async () => {
    // Validate
    if (!titleMessageId.trim()) {
      setValidationError('Title Message ID is required');
      return;
    }

    const messageId = parseInt(titleMessageId, 10);
    if (Number.isNaN(messageId)) {
      setValidationError('Title Message ID must be a number');
      return;
    }

    if (!title.trim()) {
      setValidationError('Title is required');
      return;
    }

    if (!libraryId) {
      setValidationError('Library is required');
      return;
    }

    setValidationError('');

    const payload = {
      chat_id: chatId,
      library_id: libraryId,
      title: title.trim(),
      title_message_id: messageId,
      author: author.trim() || undefined,
      ms_no: msNo.trim() || undefined,
      notes: notes.trim() || undefined,
    };

    try {
      const created = await createEntry.mutateAsync(payload);
      onCreated(created);
      onClose();
    } catch (error) {
      setValidationError(`Failed to create entry: ${(error as Error).message}`);
    }
  };

  const handleCreateMissing = async () => {
    // Validate
    if (!missingMsNo.trim()) {
      setValidationError('Manuscript Number is required');
      return;
    }

    if (!missingNotes.trim()) {
      setValidationError('Missing Notes are required - please explain why');
      return;
    }

    if (!missingLibraryId) {
      setValidationError('Library is required');
      return;
    }

    setValidationError('');

    const payload = {
      library_id: missingLibraryId,
      ms_no: missingMsNo.trim(),
      title: missingTitle.trim() || undefined,
      author: missingAuthor.trim() || undefined,
      missing_notes: missingNotes.trim(),
    };

    try {
      const created = await createMissingEntry.mutateAsync(payload);
      onCreated(created);
      onClose();
    } catch (error) {
      setValidationError(`Failed to create entry: ${(error as Error).message}`);
    }
  };

  const handleCreate = mode === 'regular' ? handleCreateRegular : handleCreateMissing;

  const selectedLibrary = useMemo(() => {
    const libId = mode === 'regular' ? libraryId : missingLibraryId;
    return libraries.find((lib) => lib.id === libId);
  }, [libraries, libraryId, missingLibraryId, mode]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('quickEntryDialog.title', 'Insert Entry')}</DialogTitle>
      <DialogContent dividers>
        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        <Tabs
          value={mode}
          onChange={(_, newValue) => {
            setMode(newValue);
            setValidationError('');
          }}
          aria-label="Entry mode selection"
        >
          <Tab
            label={t('quickEntryDialog.regularTab', 'Regular Entry')}
            value="regular"
            id="quick-entry-tab-regular"
          />
          <Tab
            label={t('quickEntryDialog.missingTab', 'Missing Entry')}
            value="missing"
            id="quick-entry-tab-missing"
          />
        </Tabs>

        {/* Regular Entry Mode */}
        <TabPanel value={mode} index="regular">
          <TextField
            label={t('quickEntryDialog.messageIdLabel', 'Title Message ID')}
            type="number"
            value={titleMessageId}
            onChange={(e) => {
              setTitleMessageId(e.target.value);
              if (validationError) setValidationError('');
            }}
            helperText={t('quickEntryDialog.messageIdHelper', `Suggested: between #${beforeMessageId} and #${afterMessageId}`)}
            required
            fullWidth
            margin="normal"
            error={validationError.includes('Message ID')}
          />

          <TextField
            label={t('quickEntryDialog.titleLabel', 'Title')}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (validationError) setValidationError('');
            }}
            helperText={t('quickEntryDialog.titleRequired', '')}
            required
            fullWidth
            margin="normal"
            inputProps={{ dir: 'rtl' }}
            error={validationError.includes('Title')}
          />

          <TextField
            label={t('quickEntryDialog.authorLabel', 'Author')}
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            fullWidth
            margin="normal"
            inputProps={{ dir: 'rtl' }}
          />

          <TextField
            label={t('quickEntryDialog.msNoLabel', 'MS Number')}
            value={msNo}
            onChange={(e) => setMsNo(e.target.value)}
            fullWidth
            margin="normal"
          />

          <Autocomplete
            options={libraries}
            getOptionLabel={(lib) => lib.canonical_name}
            value={selectedLibrary || null}
            onChange={(_, lib) => setLibraryId(lib?.id || null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('quickEntryDialog.libraryLabel', 'Library')}
                margin="normal"
                required
                error={validationError.includes('Library')}
              />
            )}
            fullWidth
          />

          <TextField
            label={t('quickEntryDialog.notesLabel', 'Notes')}
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            margin="normal"
          />
        </TabPanel>

        {/* Missing Entry Mode */}
        <TabPanel value={mode} index="missing">
          <TextField
            label={t('quickEntryDialog.msNoLabel', 'MS Number')}
            value={missingMsNo}
            onChange={(e) => {
              setMissingMsNo(e.target.value);
              if (validationError) setValidationError('');
            }}
            helperText={t('quickEntryDialog.messageIdHelper', `Suggested: between #${beforeMessageId} and #${afterMessageId}`)}
            required
            fullWidth
            margin="normal"
            error={validationError.includes('Manuscript Number')}
          />

          <TextField
            label={t('quickEntryDialog.titleLabel', 'Title')}
            value={missingTitle}
            onChange={(e) => setMissingTitle(e.target.value)}
            helperText={t('quickEntryDialog.messageIdHelper', '(if known)')}
            fullWidth
            margin="normal"
            inputProps={{ dir: 'rtl' }}
          />

          <TextField
            label={t('quickEntryDialog.authorLabel', 'Author')}
            value={missingAuthor}
            onChange={(e) => setMissingAuthor(e.target.value)}
            fullWidth
            margin="normal"
            inputProps={{ dir: 'rtl' }}
          />

          <Autocomplete
            options={libraries}
            getOptionLabel={(lib) => lib.canonical_name}
            value={libraries.find((lib) => lib.id === missingLibraryId) || null}
            onChange={(_, lib) => setMissingLibraryId(lib?.id || null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('quickEntryDialog.libraryLabel', 'Library')}
                margin="normal"
                required
                error={validationError.includes('Library')}
              />
            )}
            fullWidth
          />

          <TextField
            label={t('quickEntryDialog.missingNotesLabel', 'Missing Notes')}
            multiline
            rows={3}
            value={missingNotes}
            onChange={(e) => {
              setMissingNotes(e.target.value);
              if (validationError) setValidationError('');
            }}
            helperText={t('quickEntryDialog.missingNotesHelper', 'Required: Why is this manuscript missing?')}
            required
            fullWidth
            margin="normal"
            error={validationError.includes('Missing Notes')}
          />
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {t('quickEntryDialog.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          color="primary"
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {t('quickEntryDialog.create', 'Create Entry')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickEntryDialog;
